/**
 * workOrderStore — Supabase-backed work order store.
 * Exposes the same useWorkOrders() API as before; all data is now persisted.
 * Offline-aware: mutations fall back to IndexedDB + sync queue when offline.
 */
import React, { createContext, useContext, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { WorkOrder, WOType, WOImpact, WOStatus, Confidence, BlockedReason, ClosureData } from '@/types/maintenance';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchWorkOrders,
  createWorkOrderInDB,
  startWorkInDB,
  blockWorkOrderInDB,
  resumeWorkOrderInDB,
  closeWorkOrderInDB,
  reassignWorkOrderInDB,
  type CreateWOInput,
  type BlockWOInput,
} from '@/lib/api/workOrders';
import { db } from '@/lib/db/index';
import { enqueue } from '@/lib/sync/queue';
import { nowServer } from '@/lib/sync/serverTime';

// ─── Context types ────────────────────────────────────────────────────────────

interface WorkOrderContextType {
  workOrders: WorkOrder[];
  loading: boolean;
  createPending: boolean;
  startPending: boolean;
  blockPending: boolean;
  resumePending: boolean;
  finishPending: boolean;
  getWorkOrder: (id: string) => WorkOrder | undefined;
  getWorkOrdersByMold: (moldId: string) => WorkOrder[];
  createWorkOrder: (input: CreateWOInput) => Promise<WorkOrder>;
  getOpenWorkOrders: () => WorkOrder[];
  getBlockedWorkOrders: () => WorkOrder[];
  startWork: (woId: string, shotCount?: number) => Promise<void>;
  reassignWorkOrder: (woId: string, assignedTo: string | null) => Promise<void>;
  blockWorkOrder: (woId: string, input: BlockWOInput) => Promise<void>;
  resumeWorkOrder: (woId: string) => Promise<void>;
  finishWorkOrder: (woId: string, closureData: ClosureData) => Promise<void>;
}

const WorkOrderContext = createContext<WorkOrderContextType | null>(null);

const QUERY_KEY = ['work_orders'] as const;

// ─── Provider ─────────────────────────────────────────────────────────────────

export function WorkOrderProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const orgId = user?.organization_id ?? null;

  const { data: workOrders = [], isLoading } = useQuery({
    queryKey: [...QUERY_KEY, orgId],
    queryFn: async () => {
      if (!navigator.onLine) {
        const cached = await db.work_orders
          .where('organization_id').equals(orgId ?? '')
          .toArray();
        return cached.length > 0 ? cached : [];
      }
      return fetchWorkOrders(orgId);
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: Infinity,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }, [queryClient]);

  // ── Reads ──────────────────────────────────────────────────────────────────

  const getWorkOrder = useCallback(
    (id: string) => workOrders.find(wo => wo.id === id),
    [workOrders],
  );

  const getWorkOrdersByMold = useCallback(
    (moldId: string) => workOrders.filter(wo => wo.mold_id === moldId),
    [workOrders],
  );

  const getOpenWorkOrders = useCallback(
    () => workOrders.filter(wo => wo.status !== 'closed'),
    [workOrders],
  );

  const getBlockedWorkOrders = useCallback(
    () => workOrders.filter(wo => wo.status === 'blocked'),
    [workOrders],
  );

  // ── Mutations ──────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: async (input: CreateWOInput): Promise<WorkOrder> => {
      if (!navigator.onLine) {
        const now = nowServer();
        const tempId = crypto.randomUUID();
        const tempWO: WorkOrder = {
          id: tempId,
          wo_id: `WO-OFFLINE-${tempId.slice(0, 8)}`,
          type: input.type,
          mold_id: input.mold_id,
          machine_id: input.machine_id,
          created_by: user!.id,
          assigned_to: input.assigned_to,
          impact: input.impact,
          status: 'requested' as WOStatus,
          problem_description: input.problem_description,
          failure_text: input.failure_text,
          parts_needed: input.parts_needed,
          parts_list: input.parts_list,
          eta_to_restore: input.eta_to_restore,
          eta_confidence: input.eta_confidence,
          from_planning: input.from_planning ?? false,
          attachments: [],
          comments: [],
          status_history: [],
          created_at: now,
          updated_at: now,
          // Joined relations not available offline
          mold: undefined,
          assignee: undefined,
          machine: undefined,
        };
        await db.work_orders.put({ ...tempWO, organization_id: orgId ?? '' } as WorkOrder & { organization_id: string });
        queryClient.setQueryData([...QUERY_KEY, orgId], (old: WorkOrder[] = []) => [tempWO, ...old]);
        await enqueue({
          type: 'wo:create',
          entity_id: tempId,
          payload: { input, temp_id: tempId },
          org_id: orgId!,
          user_id: user!.id,
          optimistic_updated_at: now,
          created_at: now,
          status: 'pending',
          retry_count: 0,
        });
        toast.info('Work order created offline — will sync when connected');
        return tempWO;
      }
      return createWorkOrderInDB(input, user!.id, user!.organization_id!, undefined);
    },
    onSuccess: () => { if (navigator.onLine) invalidate(); },
    onError: (err: Error) => toast.error(`Failed to create work order: ${err.message}`),
  });

  const createWorkOrder = useCallback(
    (input: CreateWOInput): Promise<WorkOrder> =>
      createMutation.mutateAsync(input),
    [createMutation],
  );

  const startMutation = useMutation({
    mutationFn: async ({ wo, shotCount }: { wo: WorkOrder; shotCount?: number }) => {
      if (!navigator.onLine) {
        const now = nowServer();
        const patch = { status: 'in_progress' as WOStatus, start_time: wo.start_time ?? now, shot_count_at_start: shotCount, updated_at: now };
        await db.work_orders.update(wo.id, patch);
        queryClient.setQueryData([...QUERY_KEY, orgId], (old: WorkOrder[] = []) =>
          old.map(w => w.id === wo.id ? { ...w, ...patch } : w),
        );
        await enqueue({
          type: 'wo:start',
          entity_id: wo.id,
          payload: { wo, shotCount },
          org_id: orgId!,
          user_id: user!.id,
          optimistic_updated_at: now,
          created_at: now,
          status: 'pending',
          retry_count: 0,
        });
        toast.info('Saved offline — will sync when connected');
        return;
      }
      return startWorkInDB(wo, user!.id, shotCount);
    },
    onSuccess: () => { if (navigator.onLine) invalidate(); },
    onError: (err: Error) => toast.error(`Failed to start work: ${err.message}`),
  });

  const startWork = useCallback(
    async (woId: string, shotCount?: number) => {
      const wo = workOrders.find(w => w.id === woId);
      if (!wo) return;
      await startMutation.mutateAsync({ wo, shotCount });
    },
    [workOrders, startMutation],
  );

  const reassignWorkOrder = useCallback(
    async (woId: string, assignedTo: string | null) => {
      await reassignWorkOrderInDB(woId, assignedTo);
      invalidate();
    },
    [invalidate],
  );

  const blockMutation = useMutation({
    mutationFn: async ({ wo, input }: { wo: WorkOrder; input: BlockWOInput }) => {
      if (!navigator.onLine) {
        const now = nowServer();
        const patch = {
          status: 'blocked' as WOStatus,
          blocked_reason: input.blocked_reason,
          blocked_reason_text: input.blocked_reason_text,
          blocked_at: now,
          blocked_by: user!.id,
          expected_unblock_date: input.expected_unblock_date,
          blocked_notes: input.blocked_notes,
          updated_at: now,
        };
        await db.work_orders.update(wo.id, patch);
        queryClient.setQueryData([...QUERY_KEY, orgId], (old: WorkOrder[] = []) =>
          old.map(w => w.id === wo.id ? { ...w, ...patch } : w),
        );
        await enqueue({
          type: 'wo:block',
          entity_id: wo.id,
          payload: { wo, input },
          org_id: orgId!,
          user_id: user!.id,
          optimistic_updated_at: now,
          created_at: now,
          status: 'pending',
          retry_count: 0,
        });
        toast.info('Saved offline — will sync when connected');
        return;
      }
      return blockWorkOrderInDB(wo, input, user!.id);
    },
    onSuccess: () => { if (navigator.onLine) invalidate(); },
    onError: (err: Error) => toast.error(`Failed to block work order: ${err.message}`),
  });

  const blockWorkOrder = useCallback(
    async (woId: string, input: BlockWOInput) => {
      const wo = workOrders.find(w => w.id === woId);
      if (!wo) return;
      await blockMutation.mutateAsync({ wo, input });
    },
    [workOrders, blockMutation],
  );

  const resumeMutation = useMutation({
    mutationFn: async (wo: WorkOrder) => {
      if (!navigator.onLine) {
        const now = nowServer();
        const patch = {
          status: 'in_progress' as WOStatus,
          blocked_reason: undefined,
          blocked_reason_text: undefined,
          blocked_at: undefined,
          blocked_by: undefined,
          expected_unblock_date: undefined,
          blocked_notes: undefined,
          updated_at: now,
        };
        await db.work_orders.update(wo.id, patch);
        queryClient.setQueryData([...QUERY_KEY, orgId], (old: WorkOrder[] = []) =>
          old.map(w => w.id === wo.id ? { ...w, ...patch } : w),
        );
        await enqueue({
          type: 'wo:resume',
          entity_id: wo.id,
          payload: { wo },
          org_id: orgId!,
          user_id: user!.id,
          optimistic_updated_at: now,
          created_at: now,
          status: 'pending',
          retry_count: 0,
        });
        toast.info('Saved offline — will sync when connected');
        return;
      }
      return resumeWorkOrderInDB(wo, user!.id);
    },
    onSuccess: () => { if (navigator.onLine) invalidate(); },
    onError: (err: Error) => toast.error(`Failed to resume work order: ${err.message}`),
  });

  const resumeWorkOrder = useCallback(
    async (woId: string) => {
      const wo = workOrders.find(w => w.id === woId);
      if (!wo) return;
      await resumeMutation.mutateAsync(wo);
    },
    [workOrders, resumeMutation],
  );

  const closeMutation = useMutation({
    mutationFn: async ({ wo, closureData }: { wo: WorkOrder; closureData: ClosureData }) => {
      if (!navigator.onLine) {
        const now = nowServer();
        const patch = {
          status: 'closed' as WOStatus,
          end_time: now,
          closure_data: closureData,
          labor_minutes: closureData.total_labor_minutes,
          fix_type: closureData.fix_type,
          tested_validated: closureData.tested_validated ?? closureData.test_performed,
          test_type: closureData.test_type,
          repair_summary: closureData.action_taken,
          failure_text: closureData.failure ?? wo.failure_text,
          updated_at: now,
        };
        await db.work_orders.update(wo.id, patch);
        queryClient.setQueryData([...QUERY_KEY, orgId], (old: WorkOrder[] = []) =>
          old.map(w => w.id === wo.id ? { ...w, ...patch } : w),
        );
        await enqueue({
          type: 'wo:close',
          entity_id: wo.id,
          payload: { wo, closureData },
          org_id: orgId!,
          user_id: user!.id,
          optimistic_updated_at: now,
          created_at: now,
          status: 'pending',
          retry_count: 0,
        });
        toast.info('Saved offline — will sync when connected');
        return;
      }
      return closeWorkOrderInDB(wo, closureData, user!.id, orgId);
    },
    onSuccess: (result) => {
      if (navigator.onLine) invalidate();
      if (result?.pending_review) {
        toast.info('Submitted for manager review — awaiting approval');
      }
    },
    onError: (err: Error) => toast.error(`Failed to close work order: ${err.message}`),
  });

  const finishWorkOrder = useCallback(
    async (woId: string, closureData: ClosureData) => {
      const wo = workOrders.find(w => w.id === woId);
      if (!wo) return;
      await closeMutation.mutateAsync({ wo, closureData });
    },
    [workOrders, closeMutation],
  );

  return (
    <WorkOrderContext.Provider value={{
      workOrders,
      loading: isLoading,
      createPending: createMutation.isPending,
      startPending: startMutation.isPending,
      blockPending: blockMutation.isPending,
      resumePending: resumeMutation.isPending,
      finishPending: closeMutation.isPending,
      getWorkOrder,
      getWorkOrdersByMold,
      createWorkOrder,
      getOpenWorkOrders,
      getBlockedWorkOrders,
      startWork,
      blockWorkOrder,
      resumeWorkOrder,
      finishWorkOrder,
      reassignWorkOrder,
    }}>
      {children}
    </WorkOrderContext.Provider>
  );
}

export function useWorkOrders() {
  const context = useContext(WorkOrderContext);
  if (!context) throw new Error('useWorkOrders must be used within a WorkOrderProvider');
  return context;
}
